/**
 * Image upload utilities for Active Storage direct uploads
 */
import SparkMD5 from 'spark-md5'

export interface UploadedImage {
  signedId: string
  url: string
  filename: string
  contentType: string
  byteSize: number
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
}

/**
 * Get CSRF token from meta tag
 */
function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') || ''
}

/**
 * Upload a file to Active Storage and return the blob details
 */
export async function uploadImage(
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadedImage> {
  // First, create a direct upload
  const directUploadUrl = '/rails/active_storage/direct_uploads'

  // Get upload URL and signed_id from Rails
  const createResponse = await fetch(directUploadUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
      Accept: 'application/json',
    },
    body: JSON.stringify({
      blob: {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
        checksum: await computeChecksum(file),
      },
    }),
  })

  if (!createResponse.ok) {
    throw new Error('Failed to create direct upload')
  }

  const blobData = await createResponse.json()

  // Upload the file to the signed URL
  await uploadToSignedUrl(blobData.direct_upload.url, blobData.direct_upload.headers, file, onProgress)

  // Return the uploaded image info
  return {
    signedId: blobData.signed_id,
    url: `/rails/active_storage/blobs/redirect/${blobData.signed_id}/${encodeURIComponent(file.name)}`,
    filename: file.name,
    contentType: file.type,
    byteSize: file.size,
  }
}

/**
 * Upload file to the signed URL provided by Active Storage
 */
async function uploadToSignedUrl(
  url: string,
  headers: Record<string, string>,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.open('PUT', url, true)

    // Set headers from Active Storage
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        })
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    }

    xhr.onerror = () => reject(new Error('Upload failed'))
    xhr.send(file)
  })
}

/**
 * Compute MD5 checksum for file (required by Active Storage)
 */
async function computeChecksum(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const spark = new SparkMD5.ArrayBuffer()
      spark.append(e.target?.result as ArrayBuffer)
      const hash = spark.end(true) // true = return raw binary
      resolve(btoa(hash))
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Validate if file is an acceptable image
 */
export function isValidImage(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  const maxSize = 10 * 1024 * 1024 // 10MB

  return validTypes.includes(file.type) && file.size <= maxSize
}

/**
 * Get image dimensions from file
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
      URL.revokeObjectURL(img.src)
    }
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

/**
 * Create a preview URL for a file
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file)
}

/**
 * Revoke a preview URL to free memory
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url)
}
